import type {
  MalnutritionCriterion,
  MalnutritionRiskAssessment,
  MalnutritionStatus,
  PatientWeightHistory,
} from "../../types/Malnutrition";

export const MALNUTRITION_USE_MOCKS =
  process.env.REACT_APP_MALNUTRITION_MOCK !== "false";

const MOCK_LATENCY_MS = 120;

const resolve = <T>(value: T): Promise<T> =>
  new Promise((res) => setTimeout(() => res(value), MOCK_LATENCY_MS));

const ALL_STATUSES: MalnutritionStatus[] = [
  "high_risk",
  "medium_risk",
  "protocol",
  "following",
  "consult",
];

const readStatusFromUrl = (): MalnutritionStatus | null => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("status");
  if (!raw) return null;
  return (ALL_STATUSES as string[]).includes(raw)
    ? (raw as MalnutritionStatus)
    : null;
};

const PATIENT_BY_STATUS: Record<
  MalnutritionStatus,
  MalnutritionRiskAssessment["patient"]
> = {
  high_risk: {
    full_name: "Hennepin, Harden",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    sex: "M",
    current_unit: "4 East",
  },
  medium_risk: {
    full_name: "Doughty, Andrew",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    sex: "M",
    current_unit: "4 East",
  },
  protocol: {
    full_name: "Breckenridge, Andrew",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    sex: "M",
    current_unit: "4 East",
  },
  following: {
    full_name: "Granda, Andrew",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    sex: "M",
    current_unit: "4 East",
  },
  consult: {
    full_name: "Caldera, Andrew",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    sex: "M",
    current_unit: "4 East",
  },
};

const FLAGGED_CRITERIA: MalnutritionCriterion[] = [
  {
    kind: "metric",
    title: "Low BMI: 16.2",
    rows: [
      { label: "Height", value: "5'6\"" },
      { label: "Weight", value: "102 lb · standing · 4/29 7:40 AM" },
    ],
  },
  {
    kind: "quote",
    title: "Severe intake reduction",
    quote: "Poor appetite with minimal intake over the last several days.",
    source_kind: "Hospitalist note",
    source_author: "Dr. Lee, MD",
    source_timestamp: "4/29 8:12 AM",
  },
  {
    kind: "metric",
    title: "Significant weight loss",
    rows: [
      { label: "Current weight", value: "102 lb · standing · 4/29" },
      { label: "Prior weight", value: "123 lb · outpatient · 1/10" },
      {
        label: "Change",
        value: "–17.1% over ~3.5 months (≥ 10% / 6mo threshold)",
      },
    ],
  },
  {
    kind: "quote",
    title: "Subcutaneous fat loss",
    quote:
      "Marked temporal wasting and visible loss of subcutaneous fat over upper extremities.",
    source_kind: "Physician progress note",
    source_author: "Dr. Lee, MD",
    source_timestamp: "4/29 8:12 AM",
  },
];

const PROTOCOL_CRITERIA: MalnutritionCriterion[] = [
  {
    kind: "metric",
    title: "Length of stay exceeds 7 days",
    rows: [{ label: "Current LOS", value: "9 days · Admitted 4/20" }],
  },
  {
    kind: "metric",
    title: "NPO duration exceeds 5 days",
    rows: [{ label: "NPO since", value: "4/23 · 6 days" }],
  },
  {
    kind: "metric",
    title: "Active nutrition consult, not yet completed or resolved",
    rows: [
      {
        label: "Ordered",
        value: "4/22 · Reason: Malnutrition screening · Status: Pending",
      },
    ],
  },
  {
    kind: "metric",
    title: "Active tube feeding / enteral nutrition order or consult",
    rows: [
      {
        label: "Nasogastric tube feed",
        value: "Ordered 4/21 · Rate: 60 mL/hr · Status: Active",
      },
    ],
  },
  {
    kind: "metric",
    title: "Active TPN / parenteral nutrition order or consult",
    rows: [
      {
        label: "Central TPN",
        value: "Started 4/20 · 1800 kcal/day · Status: Active",
      },
    ],
  },
  {
    kind: "metric",
    title: "Low BMI",
    rows: [
      {
        label: "Ht",
        value: "5'9\" · Wt: 102 lb · BMI: 15.1 · Age: 51 · Recorded 4/29",
      },
    ],
  },
];

const CRITERIA_BY_STATUS: Record<MalnutritionStatus, MalnutritionCriterion[]> = {
  high_risk: FLAGGED_CRITERIA,
  medium_risk: FLAGGED_CRITERIA.slice(0, 2),
  protocol: PROTOCOL_CRITERIA,
  following: FLAGGED_CRITERIA.slice(0, 2),
  consult: FLAGGED_CRITERIA.slice(0, 3),
};

const WEIGHT_HISTORY: PatientWeightHistory = {
  trend_percent: -7.3,
  trend_months: 0.6,
  readings: [
    { date: "4/29", weight_lb: 102, source: "Standing", is_anchor: true },
    { date: "4/20", weight_lb: 108, source: "Bed" },
    { date: "4/12", weight_lb: 110, source: "Standing", is_anchor: true },
    { date: "4/05", weight_lb: 113, source: "Bed" },
    { date: "3/28", weight_lb: 116, source: "Standing" },
    { date: "3/15", weight_lb: 119, source: "Outpatient" },
    { date: "2/20", weight_lb: 121, source: "Outpatient" },
    { date: "1/10", weight_lb: 123, source: "Outpatient" },
  ],
};

const buildRiskAssessment = (
  status: MalnutritionStatus
): MalnutritionRiskAssessment => ({
  patient: PATIENT_BY_STATUS[status],
  status,
  criteria_met: CRITERIA_BY_STATUS[status],
});

export const mockGetMalnutritionRiskAssessment = (
  _emrPatientID: string
): Promise<MalnutritionRiskAssessment> => {
  const status = readStatusFromUrl() ?? "high_risk";
  return resolve(buildRiskAssessment(status));
};

export const mockGetPatientWeights = (
  _emrPatientID: string
): Promise<PatientWeightHistory> => resolve(WEIGHT_HISTORY);
